import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { type ComponentPropsWithRef } from 'react';

import { Flex } from '@repo/ui/Flex';
import { VisuallyHidden } from '@repo/ui/VisuallyHidden';

import s from './Loader.module.css';

export const loaderVariantOptions = ['spinner'] as const;
export type LoaderVariant = (typeof loaderVariantOptions)[number];

export const loaderSizeOptions = [
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  'font',
] as const;
export type LoaderSize = (typeof loaderSizeOptions)[number];

export type LoaderProps = ComponentPropsWithRef<'div'> & {
  className?: string;
  label?: string;
  variant?: LoaderVariant;
  size?: LoaderSize;
};

export const loaderVariants = cva(s.loader, {
  variants: {
    variant: {
      spinner: s.spinnerVariant,
    },
    size: {
      xs: s.xsSize,
      sm: s.smSize,
      md: s.mdSize,
      lg: s.lgSize,
      xl: s.xlSize,
      font: s.fontSize,
    },
  },
});

const SpinnerSvg = () => (
  <svg viewBox="0 0 8 8" fill="none" className={s.spinnerSvg}>
    <path
      d="M5.07989 1.20114C5.44745 1.34295 5.78348 1.55576 6.06879 1.82743C6.3541 2.0991 6.58311 2.42431 6.74274 2.78449C6.90238 3.14466 6.9895 3.53275 6.99915 3.9266C7.00879 4.32045 6.94077 4.71234 6.79897 5.0799C6.65716 5.44746 6.44435 5.78349 6.17267 6.0688C5.901 6.35411 5.5758 6.58312 5.21562 6.74275C4.85544 6.90239 4.46735 6.98951 4.0735 6.99916C3.67966 7.0088 3.28777 6.94078 2.92021 6.79898"
      stroke="currentColor"
      strokeLinecap="round"
      vectorEffect="non-scaling-stroke"
    />
  </svg>
);

export const Loader = (props: LoaderProps) => {
  const {
    size = 'font',
    className,
    label = 'Loading',
    variant = 'spinner',
    ...attrs
  } = props;

  return (
    <Flex
      alignItems="center"
      className={clsx(
        loaderVariants({
          size,
          variant,
          className,
        }),
      )}
      display="inline-flex"
      justifyContent="center"
      {...attrs}
    >
      <SpinnerSvg />
      <VisuallyHidden>{label}</VisuallyHidden>
    </Flex>
  );
};
